import { Component } from "@theme/component";
import { debounce, onDocumentLoaded } from "@theme/utilities";
import { MegaMenuHoverEvent } from "@theme/events";

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * HeaderMenu manages hover activation for top-nav items and their submenus.
 * Ensures only one submenu is visible at a time (Sneakers mega vs Help tiny).
 */
class HeaderMenu extends Component {
    // Do NOT require optional refs to avoid runtime errors on variants
    requiredRefs = undefined;

    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        // Hover-out anywhere on the header area closes the active menu
        this.addEventListener("pointerleave", () => this.#debouncedDeactivate(), {
            signal: this.#abortController.signal,
        });

        // Keep menu open while hovering any submenu container or inner panel
        const submenus = this.#getAllSubmenus();
        submenus.forEach((sm) => {
            sm.addEventListener?.("pointerenter", () => this.#debouncedDeactivate.cancel(), {
                signal: this.#abortController.signal,
            });
            sm.addEventListener?.("pointerleave", () => this.#debouncedDeactivate(), {
                signal: this.#abortController.signal,
            });
            const inner = sm.querySelector(".menu-list__submenu-inner");
            inner?.addEventListener?.("pointerenter", () => this.#debouncedDeactivate.cancel(), {
                signal: this.#abortController.signal,
            });
            inner?.addEventListener?.("pointerleave", () => this.#debouncedDeactivate(), {
                signal: this.#abortController.signal,
            });
        });

        onDocumentLoaded(this.#preloadImages);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    /** @type {{ activeItem: HTMLElement | null }} */
    #state = { activeItem: null };

    get animationDelay() {
        const value = this.dataset.animationDelay;
        return value ? parseInt(value, 10) : 0;
    }

    /** Activate (debounced) from declarative listeners */
    activate = (event) => this.#debouncedActivateHandler(event);

    /** Deactivate (debounced) from declarative listeners */
    deactivate(event) {
        this.#debouncedActivateHandler.cancel();
        const item = this.#getMenuItemFromEvent(event);
        if (item === this.#state.activeItem) this.#debouncedDeactivate();
    }

    /** Real activation handler */
    #activateHandler = (event) => {
        this.#debouncedDeactivate.cancel();
        this.dispatchEvent(new MegaMenuHoverEvent());

        const item = this.#getMenuItemFromEvent(event);
        if (!item || item === this.#state.activeItem) return;

        // Close previous
        this.#state.activeItem?.setAttribute("aria-expanded", "false");

        // Determine which submenu to open (by key)
        const key = item.closest("[data-menu-item]")?.getAttribute("data-menu-item");
        const submenu = key ? this.querySelector(`[data-submenu-for="${key}"]`) : null;

        // Hide all submenus and mark the active one
        const allSubmenus = this.#getAllSubmenus();
        allSubmenus.forEach((sm) => {
            if (sm === submenu) sm.setAttribute("data-active", "true");
            else sm.removeAttribute("data-active");
        });

        // Position below header and horizontally align with the trigger
        const headerRect = this.getBoundingClientRect();
        this.style.setProperty("--submenu-top", `${Math.round(headerRect.bottom)}px`);

        const triggerRect = item.getBoundingClientRect();
        this.style.setProperty("--submenu-left", `${Math.round(triggerRect.left)}px`);

        // Update height for transition of mega menu (help panel uses its own fixed inner box)
        const submenuHeight = submenu ? submenu.offsetHeight : 0;
        this.style.setProperty("--submenu-height", `${submenuHeight}px`);
        this.style.setProperty("--submenu-opacity", "1");

        this.#state.activeItem = item;
        this.setAttribute("aria-expanded", "true");
        item.setAttribute("aria-expanded", "true");
    };

    #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);
    #debouncedDeactivate = debounce(() => this.#deactivate(), DEACTIVATE_DELAY);

    #deactivate = () => {
        // Keep open if hovering any submenu/panel
        const hovering = this.#getAllSubmenus().some(
            (sm) => sm.matches(":hover") || sm.querySelector(".menu-list__submenu-inner")?.matches?.(":hover")
        );
        if (hovering) return;

        this.style.setProperty("--submenu-height", "0px");
        this.style.setProperty("--submenu-opacity", "0");

        this.#getAllSubmenus().forEach((sm) => sm.removeAttribute("data-active"));

        this.removeAttribute("aria-expanded");
        this.#state.activeItem?.setAttribute("aria-expanded", "false");
        this.#state.activeItem = null;
    };

    #preloadImages = () => {
        const images = this.querySelectorAll('img[loading="lazy"]');
        images?.forEach((image) => image.removeAttribute("loading"));
    };

    #getAllSubmenus() {
        return /** @type {HTMLElement[]} */ ([
            ...this.querySelectorAll('[ref="submenu[]"]'),
            ...this.querySelectorAll('[ref="unifiedSubmenu"]'),
        ]);
    }

    #getMenuItemFromEvent(event) {
        const target = /** @type {Element} */ (event?.target);
        const anchor = target?.closest?.('[ref="menuitem"]');
        return /** @type {HTMLElement | null} */ (anchor || null);
    }
}

if (!customElements.get("header-menu")) {
    customElements.define("header-menu", HeaderMenu);
}
